import { useState } from 'react';
import { RiMagicLine, RiFileCopyLine, RiCheckLine, RiLoader4Line, RiArrowDownSLine } from 'react-icons/ri';
import GlassCard from '../../components/ui/v2/GlassCard';
import GlassButton from '../../components/ui/v2/GlassButton';
import { messageService } from '../../services/messageService';
import { creditService } from '../../services/creditService';
import type { MessageIntent, MessageTone, MessageLength } from '../../models/message';
import { toast } from 'react-hot-toast';

// --- Constants ---

const INTENTS: { value: MessageIntent; label: string }[] = [
    { value: 'introduce_myself', label: 'Introduce myself' },
    { value: 'ask_for_referral', label: 'Ask for referral' },
    { value: 'email_recruiter', label: 'Email recruiter' },
    { value: 'email_founder', label: 'Email founder' },
    { value: 'follow_up', label: 'Follow up' },
    { value: 'thank_you', label: 'Thank you' },
    { value: 'other', label: 'Other' },
];

const TONES: { value: MessageTone; label: string }[] = [
    { value: 'professional', label: 'Professional' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'confident', label: 'Confident' },
    { value: 'polite', label: 'Polite' },
    { value: 'casual', label: 'Casual' },
];

const LENGTHS: { value: MessageLength; label: string }[] = [
    { value: 'very_short', label: 'Very short' },
    { value: 'short', label: 'Short' },
    { value: 'medium', label: 'Medium' },
];

export default function MessageComposer() {
    // --- State ---
    const [intent, setIntent] = useState<MessageIntent>('introduce_myself');
    const [tone, setTone] = useState<MessageTone>('professional');
    const [length, setLength] = useState<MessageLength>('short');

    // Context form
    const [context, setContext] = useState({
        recipient_name: '',
        recipient_role: '',
        company_name: '',
        instructions: ''
    });

    const [isContextExpanded, setIsContextExpanded] = useState(false);

    // Result State
    const [generatedMessage, setGeneratedMessage] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [hasCopied, setHasCopied] = useState(false);

    // --- Handlers ---

    const handleGenerate = async () => {
        setIsGenerating(true);
        setHasCopied(false);
        try {
            const response = await messageService.generateMessage({
                intent,
                tone,
                length,
                ...context
            });
            creditService.invalidate();
            setGeneratedMessage(response.content);
        } catch (error: any) {
            console.error(error);
            if (error?.response?.status === 402) {
                toast.error("You need more credits to use this feature.");
                // TODO: Redirect to payment or show upgrade modal
            } else {
                toast.error("Couldn't generate message. Try again.");
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopy = () => {
        if (!generatedMessage) return;
        navigator.clipboard.writeText(generatedMessage);
        setHasCopied(true);
        toast.success("Copied to clipboard");

        // Reset checkmark after specific time
        setTimeout(() => setHasCopied(false), 2000);
    };

    return (
        <div className="max-w-4xl mx-auto pb-20 animate-fade-in space-y-6">

            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-white mb-2">Message Composer</h1>
                <p className="text-gray-400">
                    Pick what you want to say, choose the tone and length, generate, copy, send.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* --- Left Column: Inputs --- */}
                <div className="space-y-6">
                    <GlassCard className="space-y-6">
                        {/* 1. Intent */}
                        <div className="space-y-3">
                            <label className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                Message Intent <span className="text-red-400">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    value={intent}
                                    onChange={(e) => setIntent(e.target.value as MessageIntent)}
                                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white appearance-none outline-none focus:border-white/50 transition-all font-medium"
                                >
                                    {INTENTS.map((opt) => (
                                        <option key={opt.value} value={opt.value} className="text-black">
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <RiArrowDownSLine className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                        </div>

                        {/* 2. Style (Tone & Length) */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tone</label>
                                <div className="relative">
                                    <select
                                        value={tone}
                                        onChange={(e) => setTone(e.target.value as MessageTone)}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white appearance-none outline-none focus:border-white/50 text-sm"
                                    >
                                        {TONES.map((opt) => (
                                            <option key={opt.value} value={opt.value} className="text-black">
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    <RiArrowDownSLine className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Length</label>
                                <div className="relative">
                                    <select
                                        value={length}
                                        onChange={(e) => setLength(e.target.value as MessageLength)}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white appearance-none outline-none focus:border-white/50 text-sm"
                                    >
                                        {LENGTHS.map((opt) => (
                                            <option key={opt.value} value={opt.value} className="text-black">
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    <RiArrowDownSLine className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                                </div>
                            </div>
                        </div>

                        {/* 3. Optional Context (Collapsible) */}
                        <div className="pt-2 border-t border-white/5">
                            <button
                                onClick={() => setIsContextExpanded(!isContextExpanded)}
                                className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors w-full py-2"
                            >
                                <RiArrowDownSLine className={`transition-transform ${isContextExpanded ? 'rotate-180' : ''}`} />
                                Optional details (improves accuracy)
                            </button>

                            {isContextExpanded && (
                                <div className="space-y-4 pt-4 animate-fade-in">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input
                                            type="text"
                                            placeholder="Recipient Name"
                                            value={context.recipient_name}
                                            onChange={(e) => setContext({ ...context, recipient_name: e.target.value })}
                                            className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/50"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Recipient Role"
                                            value={context.recipient_role}
                                            onChange={(e) => setContext({ ...context, recipient_role: e.target.value })}
                                            className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/50"
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Company Name"
                                        value={context.company_name}
                                        onChange={(e) => setContext({ ...context, company_name: e.target.value })}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/50"
                                    />
                                    <textarea
                                        placeholder="Specific instructions (e.g. mention successful Series A funding)"
                                        value={context.instructions}
                                        onChange={(e) => setContext({ ...context, instructions: e.target.value })}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/50 h-24 resize-none"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Generate Button */}
                        <div className="pt-2">
                            <GlassButton
                                onClick={handleGenerate}
                                className="w-full flex justify-center items-center gap-2 !py-3 !bg-white !text-black hover:!bg-gray-200 !shadow-[0_0_20px_rgba(255,255,255,0.1)] !border-none"
                                disabled={isGenerating}
                            >
                                {isGenerating ? (
                                    <RiLoader4Line className="animate-spin text-black" size={20} />
                                ) : (
                                    <>
                                        <RiMagicLine size={20} />
                                        Generate Message
                                    </>
                                )}
                            </GlassButton>
                        </div>
                    </GlassCard>
                </div>

                {/* --- Right Column: Result --- */}
                <div className="h-full">
                    <GlassCard className="h-full min-h-[400px] flex flex-col relative !p-0 overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-white/5 bg-white/5">
                            <span className="text-sm font-medium text-gray-400">Generated Result</span>
                            {generatedMessage && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleGenerate}
                                        disabled={isGenerating}
                                        className="text-xs text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                                    >
                                        Regenerate
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="flex-1 p-6 relative bg-black/20">
                            {isGenerating ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 bg-black/50 backdrop-blur-sm z-10 transition-all">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-white/10 blur-xl rounded-full" />
                                        <RiLoader4Line className="animate-spin mb-3 text-white relative z-10" size={32} />
                                    </div>
                                    <p className="text-sm font-medium">Crafting your message...</p>
                                </div>
                            ) : generatedMessage ? (
                                <div className="h-full overflow-y-auto custom-scrollbar">
                                    <p className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-gray-200 selection:bg-white/20">
                                        {generatedMessage}
                                    </p>
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-gray-500 select-none">
                                    <RiMagicLine size={48} className="mb-4 opacity-10" />
                                    <p className="text-center max-w-[200px] text-sm">
                                        Your generated message will appear here ready to copy.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Footer Action */}
                        {generatedMessage && !isGenerating && (
                            <div className="p-4 border-t border-white/5 bg-white/5 flex justify-end">
                                <button
                                    onClick={handleCopy}
                                    className={`
                                        flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all
                                        ${hasCopied
                                            ? 'bg-green-500 text-white shadow-lg shadow-green-500/20'
                                            : 'bg-white text-black hover:bg-gray-200 shadow-lg shadow-white/10'
                                        }
                                    `}
                                >
                                    {hasCopied ? (
                                        <>
                                            <RiCheckLine size={18} />
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <RiFileCopyLine size={18} />
                                            Copy to Clipboard
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </GlassCard>
                </div>
            </div>
        </div>
    );
}
